import { useState } from 'react'
import ContextMenu from '../ContextMenu'
import type { BossShortcut, BossShortcutGroup } from '../../types/boss'

// ── Shared badge row ──────────────────────────────────────────────────

/**
 * The four report markers the shortcut panel and the report picker both draw.
 * It lives here rather than in a new shared file because this slice owns only
 * three files; one definition with two callers still beats copying the markup.
 */
export function BossBadges({ isCore, hasPreFilters, hasMyStore, needsReview }: {
    isCore?: boolean
    hasPreFilters?: boolean
    hasMyStore?: boolean
    needsReview?: boolean
}) {
    return (
        <>
            {isCore && <span className="boss-badge boss-badge-core" title="Core report">core</span>}
            {hasPreFilters && (
                <span className="boss-badge boss-badge-filter" title="This report has pre-filters">filters</span>
            )}
            {hasMyStore && (
                <span className="boss-badge boss-badge-mystore" title="Filters on the store you pick">my store</span>
            )}
            {needsReview && (
                <span className="boss-badge boss-badge-review" title="A designer must fix this report before it can run">
                    needs review
                </span>
            )}
        </>
    )
}

// ── Reorder helpers ───────────────────────────────────────────────────

/** One shortcut's new home after a drag — the wire shape of `PUT /shortcuts/order`. */
export type ShortcutOrderItem = { id: string; groupId: string; sortOrder: number }

/**
 * Moves `dragId` into `targetGroupId`, ahead of `beforeId` (or to the end when it is null),
 * and renumbers every group's `sortOrder` from zero. Pure, so the caller can hand the result
 * straight to an optimistic cache write and derive the request body from it.
 */
export function moveShortcut(
    groups: BossShortcutGroup[],
    dragId: string,
    targetGroupId: string,
    beforeId: string | null,
): BossShortcutGroup[] {
    const moved = groups.flatMap(g => g.shortcuts).find(s => s.id === dragId)
    if (!moved || dragId === beforeId) return groups

    return groups
        .map(g => ({ ...g, shortcuts: g.shortcuts.filter(s => s.id !== dragId) }))
        .map(g => {
            if (g.id !== targetGroupId) {
                return { ...g, shortcuts: g.shortcuts.map((s, i) => ({ ...s, sortOrder: i })) }
            }
            const next: BossShortcut[] = [...g.shortcuts]
            const at = beforeId ? next.findIndex(s => s.id === beforeId) : -1
            if (at >= 0) next.splice(at, 0, moved)
            else next.push(moved)
            return { ...g, shortcuts: next.map((s, i) => ({ ...s, sortOrder: i })) }
        })
}

/** Flattens a group list into the `shortcuts` array `PUT /shortcuts/order` expects. */
export function orderOf(groups: BossShortcutGroup[]): ShortcutOrderItem[] {
    return groups.flatMap(g => g.shortcuts.map((s, i) => ({ id: s.id, groupId: g.id, sortOrder: i })))
}

// ── Panel ─────────────────────────────────────────────────────────────

export type ShortcutPanelProps = {
    groups: BossShortcutGroup[]
    /** The report currently open, so its shortcut can render as active. */
    selectedReportId: string
    loading?: boolean
    /** Already unwrapped by the page's errorMessage(); rendered verbatim. */
    error?: string | null
    /** True while a shortcut mutation is in flight — the panel's controls go read-only. */
    busy?: boolean
    onSelect: (reportId: string) => void
    /**
     * "Load […] with filters…" — open the report and ask for pre-filters first, on any report and
     * on any of its fields. The legacy tool put this on the same right-click
     * (MainForm.vb OpenWithFilterToolStripMenuItem), and what the user picks applies to that run
     * alone rather than to the report's definition.
     */
    onLoadWithFilters: (reportId: string) => void
    onReorder: (groups: BossShortcutGroup[], order: ShortcutOrderItem[]) => void
    onAddGroup: (name: string) => void
    onRenameGroup: (groupId: string, name: string) => void
    onDeleteGroup: (groupId: string) => void
    onRemoveShortcut: (shortcutId: string) => void
    /**
     * boss.reports.delete. Cosmetic only — DELETE /reports/{id} is gated on the same key on the
     * server, so hiding the item never decides anything.
     */
    canDeleteReports?: boolean
    /**
     * "Permanently Delete Report" — destroys the DEFINITION everyone shares, not this shortcut.
     * Asks for the confirm dialog; the page owns it, because what it deletes outlives this panel.
     */
    onDeleteReport?: (reportId: string, name: string) => void
    onBrowse: () => void
}

/**
 * The legacy report tree, ported: user-owned groups of pinned reports, reorderable by drag
 * both inside a group and between groups.
 *
 * Drag and drop is raw HTML5 DnD with the dragged id held in React state and nothing in
 * `dataTransfer` — the same pattern RoleAdminPage uses for role reordering. No DnD library.
 *
 * Renders CONTENTS ONLY: the frame, the title and the pop-out control come from `BossPanel`, which
 * draws this either in the page layout or floating over the grid. Owning its own `<aside>` would
 * have meant two frames to keep in step once it could be in two places.
 */
export default function ShortcutPanel({
    groups, selectedReportId, loading, error, busy,
    onSelect, onLoadWithFilters, onReorder, onAddGroup, onRenameGroup, onDeleteGroup, onRemoveShortcut,
    canDeleteReports = false, onDeleteReport, onBrowse,
}: ShortcutPanelProps) {
    const [draggingId, setDraggingId] = useState<string | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)
    const [dropGroupId, setDropGroupId] = useState<string | null>(null)
    const [adding, setAdding] = useState(false)
    const [newName, setNewName] = useState('')
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editName, setEditName] = useState('')

    const sorted = groups
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(g => ({ ...g, shortcuts: g.shortcuts.slice().sort((a, b) => a.sortOrder - b.sortOrder) }))

    const clearDrag = () => { setDraggingId(null); setDragOverId(null); setDropGroupId(null) }

    const drop = (targetGroupId: string, beforeId: string | null) => {
        if (!draggingId) return
        const next = moveShortcut(sorted, draggingId, targetGroupId, beforeId)
        clearDrag()
        if (next !== sorted) onReorder(next, orderOf(next))
    }

    const submitNewGroup = () => {
        const name = newName.trim()
        if (!name) return
        onAddGroup(name)
        setNewName('')
        setAdding(false)
    }

    const submitRename = (groupId: string) => {
        const name = editName.trim()
        setEditingId(null)
        if (name) onRenameGroup(groupId, name)
    }

    return (
        <>
            {loading && <div className="boss-muted">Loading…</div>}
            {error && <div className="boss-error">{error}</div>}
            {!loading && !error && sorted.length === 0 && (
                <div className="boss-muted">No shortcuts yet — browse the reports and pin the ones you use.</div>
            )}

            {sorted.map(g => (
                <div
                    key={g.id}
                    className={`boss-group${dropGroupId === g.id ? ' drop-target' : ''}`}
                    onDragOver={e => { if (draggingId) { e.preventDefault(); setDropGroupId(g.id) } }}
                    onDragLeave={() => setDropGroupId(cur => (cur === g.id ? null : cur))}
                    onDrop={e => { e.preventDefault(); drop(g.id, null) }}
                >
                    <div className="boss-group-head">
                        {editingId === g.id ? (
                            <input
                                className="form-input"
                                autoFocus
                                maxLength={30}
                                value={editName}
                                onChange={e => setEditName(e.target.value)}
                                onBlur={() => submitRename(g.id)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') submitRename(g.id)
                                    if (e.key === 'Escape') setEditingId(null)
                                }}
                            />
                        ) : (
                            <>
                                <div className="boss-group-label">{g.name}</div>
                                <div className="boss-group-actions">
                                    <button
                                        type="button"
                                        className="boss-icon-btn"
                                        title="Rename this group"
                                        aria-label={`Rename ${g.name}`}
                                        disabled={busy}
                                        onClick={() => { setEditingId(g.id); setEditName(g.name) }}
                                    >✎</button>
                                    <button
                                        type="button"
                                        className="boss-icon-btn"
                                        title="Delete this group"
                                        aria-label={`Delete ${g.name}`}
                                        disabled={busy}
                                        onClick={() => onDeleteGroup(g.id)}
                                    >×</button>
                                </div>
                            </>
                        )}
                    </div>

                    {g.shortcuts.length === 0 && (
                        <div className="boss-muted">Drop a report here.</div>
                    )}

                    {/* The tooltip below is the report's name, never its memo. In this corpus the
                        legacy Memo field was used as a scratchpad for the report's own SQL, so a
                        hover would dump a query across the panel. */}
                    {g.shortcuts.map(s => (
                        <ContextMenu
                            key={s.id}
                            items={[
                                { label: `Load "${s.label}" with filters…`, icon: '⧩', onClick: () => onLoadWithFilters(s.reportId) },
                                { label: 'Open', icon: '▸', onClick: () => onSelect(s.reportId) },
                                { label: 'Remove shortcut', icon: '×', disabled: busy, onClick: () => onRemoveShortcut(s.id) },
                                // Last, and deliberately worded apart from "Remove shortcut" right
                                // above it: one unpins the report from this panel, the other
                                // destroys the definition for everybody.
                                ...(canDeleteReports
                                    ? [{
                                        label: 'Permanently Delete Report', icon: '🗑', disabled: busy,
                                        onClick: () => onDeleteReport?.(s.reportId, s.label),
                                    }]
                                    : []),
                            ]}
                        >
                        <div className="boss-shortcut-row">
                            <button
                                type="button"
                                className={`boss-shortcut${selectedReportId === s.reportId ? ' active' : ''}`
                                    + `${draggingId === s.id ? ' dragging' : ''}`
                                    + `${dragOverId === s.id && draggingId !== s.id ? ' drag-over' : ''}`}
                                title={s.label}
                                draggable
                                onClick={() => onSelect(s.reportId)}
                                onDragStart={() => setDraggingId(s.id)}
                                onDragOver={e => {
                                    if (!draggingId) return
                                    // Stop here so the group's own handler does not also light up:
                                    // dropping on a shortcut inserts before it, not at the end.
                                    e.preventDefault()
                                    e.stopPropagation()
                                    setDropGroupId(null)
                                    setDragOverId(s.id)
                                }}
                                onDragLeave={() => setDragOverId(cur => (cur === s.id ? null : cur))}
                                onDrop={e => { e.preventDefault(); e.stopPropagation(); drop(g.id, s.id) }}
                                onDragEnd={clearDrag}
                            >
                                <span className="boss-drag-handle" aria-hidden="true">⠿</span>
                                <span className="boss-shortcut-label">{s.label}</span>
                                {(s.isCore || s.hasPreFilters || s.hasMyStore || s.needsReview) && (
                                    <span className="boss-shortcut-badges">
                                        <BossBadges
                                            isCore={s.isCore}
                                            hasPreFilters={s.hasPreFilters}
                                            hasMyStore={s.hasMyStore}
                                            needsReview={s.needsReview}
                                        />
                                    </span>
                                )}
                            </button>
                            <button
                                type="button"
                                className="boss-shortcut-remove"
                                title="Remove this shortcut"
                                aria-label={`Remove ${s.label}`}
                                disabled={busy}
                                onClick={() => onRemoveShortcut(s.id)}
                            >×</button>
                        </div>
                        </ContextMenu>
                    ))}
                </div>
            ))}

            {/* Naming a group takes the whole foot; otherwise the two controls share the row. */}
            <div className="boss-panel-foot">
                {adding ? (
                    <div className="boss-group-form">
                        <input
                            className="form-input"
                            autoFocus
                            maxLength={30}
                            placeholder="Group name"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') submitNewGroup()
                                if (e.key === 'Escape') { setAdding(false); setNewName('') }
                            }}
                        />
                        <button type="button" className="btn btn-primary boss-btn-sm" onClick={submitNewGroup}>
                            Add
                        </button>
                    </div>
                ) : (
                    <>
                        <button
                            type="button"
                            className="btn btn-primary boss-btn-sm boss-browse-btn"
                            onClick={onBrowse}
                        >Browse reports…</button>
                        <button
                            type="button"
                            className="btn btn-secondary boss-btn-sm boss-group-btn"
                            title="Add a shortcut group"
                            disabled={busy}
                            onClick={() => setAdding(true)}
                        >＋ Group</button>
                    </>
                )}
            </div>
        </>
    )
}
